/**
 * TestingConfig - Simple testing flags that actually work
 * 
 * 🎯 Only contains flags that are ACTUALLY connected to code
 * No fake parameters that do nothing!
 * 
 * Usage:
 *   TestingConfig.labels = false;           // Disable all labels (startup only)
 *   TestingConfig.labelConnectors = false;  // Disable only connector lines
 *   TestingConfig.labelMeshPoints = false;  // Disable only mesh point indicators
 *   TestingConfig.performance = true;       // Enable FPS monitor
 *   TestingConfig.disableUI = true;         // Hide all UI
 *   TestingConfig.showDebugConsole = false; // Hide logger by default
 */
export class TestingConfig {
    
    // 🏷️ LABELS - connected to POI.ts (startup configuration only)
    /** Global enable/disable for ALL labels (startup only - not runtime toggle) */
    public static labels = true;
    
    /** Enable/disable connector lines between POIs and labels (performance testing) */
    public static labelConnectors = false;
    
    /** Enable/disable mesh point indicators (performance testing) */
    public static labelMeshPoints = false;
    
    // ⚡ PERFORMANCE - connected to EngineDemoApp.ts  
    /** Enable FPS performance monitor (actually works!) */
    public static performance = false;
    
    // 🎨 UI - connected to EngineDemoUI.tsx
    /** Disable entire UI for pure 3D testing (actually works!) */
    public static disableUI = false;

    // 📊 DEBUG CONSOLE - connected to EngineDemoUI.tsx
    /** Show debug console by default (F2 always toggles it) */
    public static showDebugConsole = false;

    // 🚀 QUICK HELPERS
    
    /**
     * Performance mode - disable labels for max FPS
     */
    public static enablePerformanceMode(): void {
        this.labels = false;
        this.performance = true;
        this.showDebugConsole = false;
        console.log('🚀 Performance mode: Labels OFF, Monitor ON, Debug OFF');
    }

    /**
     * Minimal mode - disable everything 
     */
    public static enableMinimalMode(): void {
        this.labels = false;
        this.performance = true;
        this.disableUI = true;
        this.showDebugConsole = false;
        console.log('🎯 Minimal mode: Everything OFF except FPS monitor');
    }

    /**
     * Full mode - enable everything
     */
    public static enableFullMode(): void {
        this.labels = true;
        this.performance = false;
        this.disableUI = false;
        this.showDebugConsole = true;
        console.log('✨ Full mode: Everything ON');
    }

    /**
     * Debug mode - enable debug visualizations
     */
    public static enableDebugMode(): void {
        this.labels = true;
        this.performance = true;
        this.showDebugConsole = true;
        console.log('🔍 Debug mode: All visualizations ON');
    }

    /**
     * Get current status
     */
    public static status(): string {
        return `🎛️ TestingConfig: Labels=${this.labels}, Performance=${this.performance}, UI=${!this.disableUI}, Debug=${this.showDebugConsole}`;
    }
}

// Make available globally for console access
if (typeof window !== 'undefined') {
    (window as any).TestingConfig = TestingConfig;
    console.log('🎛️ TestingConfig available - try TestingConfig.status()');
} 